/**
 * Transcript helpers shared by the multicam studio and the editor's
 * workspace: placing lines on a timeline, finding the line being spoken,
 * searching, and caption/text exports.
 *
 * The API times lines in seconds of the video file they came from. On a
 * multicam timeline that file is the sound angle, so a line's timeline time
 * is its file time minus the angle's trim, plus where the angle starts.
 */

import type { TranscriptLineData, TranscriptWordData } from '../graphqlService';

export interface TimelineLine {
  /** Index in the transcript, stable for keys. */
  index: number;
  /** Timeline seconds. */
  start: number;
  end: number;
  text: string;
  words?: TranscriptWordData[];
  /** Who says it, when that's known (a multicam set's speaking activity). */
  speaker?: string;
}

/** Who's speaking over a stretch of the timeline (seconds), or null when unsure. */
export type LineSpeaker = (start: number, end: number) => string | null;

/** Where an angle (or a whole single video) sits on the timeline, in ms. */
export interface TranscriptPlacement {
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs?: number | null;
}

/** File seconds → timeline seconds, or null when the moment is trimmed off the timeline. */
export const placeOnTimeline = (seconds: number, placement?: TranscriptPlacement | null): number | null => {
  if (!placement) return seconds;
  const inS = Math.max(0, placement.sourceInMs || 0) / 1000;
  const outS = typeof placement.sourceOutMs === 'number' && placement.sourceOutMs > 0 ? placement.sourceOutMs / 1000 : Infinity;
  if (seconds < inS - 0.5 || seconds > outS) return null;
  return Math.max(0, seconds - inS + (placement.timelineStartMs || 0) / 1000);
};

/**
 * The transcript's lines on the timeline, dropping those trimmed off it, with
 * who says each one when `speakerAt` can tell.
 */
export const linesOnTimeline = (
  lines: TranscriptLineData[],
  placement?: TranscriptPlacement | null,
  speakerAt?: LineSpeaker
): TimelineLine[] => {
  const out: TimelineLine[] = [];
  lines.forEach((line, index) => {
    const start = placeOnTimeline(line.start, placement);
    if (start === null) return;
    const end = placeOnTimeline(line.end, placement) ?? start + Math.max(0, line.end - line.start);
    const words = line.words
      ?.map((word) => {
        const at = placeOnTimeline(word.start, placement);
        return at === null ? null : { text: word.text, start: at, end: placeOnTimeline(word.end, placement) ?? at };
      })
      .filter((word): word is TranscriptWordData => word !== null);
    const speaker = speakerAt ? speakerAt(start, Math.max(start, end)) : null;
    out.push({
      index,
      start,
      end: Math.max(start, end),
      text: line.text,
      ...(words && words.length ? { words } : {}),
      ...(speaker ? { speaker } : {}),
    });
  });
  return out;
};

/**
 * The line being spoken at `seconds`: the last line that has started, while
 * it lasts or until the next one starts (short pauses keep the line lit).
 */
export const activeLineIndex = (lines: TimelineLine[], seconds: number, holdS = 1.5): number => {
  let low = 0;
  let high = lines.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    if (lines[middle].start <= seconds + 0.05) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  if (found < 0) return -1;
  const line = lines[found];
  const next = lines[found + 1];
  const until = Math.min(line.end + holdS, next ? next.start : Infinity);
  return seconds <= until ? found : -1;
};

/** Lines whose text has every word of the query (case- and accent-insensitive). */
export const searchLines = (lines: TimelineLine[], query: string): number[] => {
  const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const terms = fold(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const hits: number[] = [];
  lines.forEach((line, position) => {
    const text = fold(line.text);
    if (terms.every((term) => text.includes(term))) hits.push(position);
  });
  return hits;
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

const clock = (seconds: number, separator: ',' | '.') => {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const ms = totalMs % 1000;
  const totalSeconds = Math.floor(totalMs / 1000);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  const two = (value: number) => String(value).padStart(2, '0');
  return `${two(h)}:${two(m)}:${two(s)}${separator}${String(ms).padStart(3, '0')}`;
};

export interface CaptionCue {
  start: number;
  end: number;
  /** One or two lines. */
  text: string;
  /** Who says it, when known. */
  speaker?: string;
}

const MAX_CUE_CHARS = 84;
const MAX_CUE_SECONDS = 7;
const MAX_LINE_CHARS = 42;

/** Breaks cue text into at most two lines of about 42 characters, at the space nearest the middle. */
export const wrapCaption = (text: string): string => {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= MAX_LINE_CHARS) return clean;
  const middle = clean.length / 2;
  let best = -1;
  for (let i = 0; i < clean.length; i += 1) {
    if (clean[i] === ' ' && (best < 0 || Math.abs(i - middle) < Math.abs(best - middle))) best = i;
  }
  return best < 0 ? clean : `${clean.slice(0, best)}\n${clean.slice(best + 1)}`;
};

/**
 * Caption cues: each line, split where it runs past about 84 characters or 7
 * seconds, at word boundaries with the words' own times.
 */
export const captionCues = (lines: TimelineLine[]): CaptionCue[] => {
  const cues: CaptionCue[] = [];
  for (const line of lines) {
    const words = line.words ?? [];
    const speaker = line.speaker ? { speaker: line.speaker } : {};
    if (line.text.length <= MAX_CUE_CHARS && line.end - line.start <= MAX_CUE_SECONDS) {
      cues.push({ start: line.start, end: line.end, text: wrapCaption(line.text), ...speaker });
      continue;
    }
    if (words.length === 0) {
      // No word times: share the line's time across pieces by length.
      const parts: string[] = [];
      let current = '';
      for (const token of line.text.split(' ')) {
        if (current && `${current} ${token}`.length > MAX_CUE_CHARS) {
          parts.push(current);
          current = token;
        } else {
          current = current ? `${current} ${token}` : token;
        }
      }
      if (current) parts.push(current);
      const total = parts.reduce((sum, part) => sum + part.length, 0) || 1;
      let at = line.start;
      for (const part of parts) {
        const length = ((line.end - line.start) * part.length) / total;
        cues.push({ start: at, end: at + length, text: wrapCaption(part), ...speaker });
        at += length;
      }
      continue;
    }
    let group: TranscriptWordData[] = [];
    const flush = () => {
      if (group.length === 0) return;
      cues.push({
        start: group[0].start,
        end: group[group.length - 1].end,
        text: wrapCaption(group.map((word) => word.text).join(' ')),
        ...speaker,
      });
      group = [];
    };
    for (const word of words) {
      const text = [...group, word].map((w) => w.text).join(' ');
      if (group.length > 0 && (text.length > MAX_CUE_CHARS || word.end - group[0].start > MAX_CUE_SECONDS)) flush();
      group.push(word);
    }
    flush();
  }
  // No cue may run into the next one.
  for (let i = 0; i < cues.length - 1; i += 1) {
    if (cues[i].end > cues[i + 1].start) cues[i].end = Math.max(cues[i].start, cues[i + 1].start);
  }
  return cues;
};

/**
 * Captions, without who speaks: an SRT becomes the captions people see (in
 * the NLE and on YouTube), where a name on every caption gets in the way and
 * a wrong guess would show on screen. The VTT and the text carry the names.
 */
export const toSrt = (lines: TimelineLine[]): string =>
  `${captionCues(lines)
    .map((cue, i) => `${i + 1}\n${clock(cue.start, ',')} --> ${clock(cue.end, ',')}\n${cue.text}\n`)
    .join('\n')}`;

/** A voice name as WebVTT allows it: one line, with &, < and > escaped. */
const vttVoice = (name: string) =>
  name.replace(/[\r\n]+/g, ' ').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Web captions, with who speaks each cue as a voice span (<v Name>) where known. */
export const toWebVtt = (lines: TimelineLine[]): string =>
  `WEBVTT\n\n${captionCues(lines)
    .map((cue) => `${clock(cue.start, '.')} --> ${clock(cue.end, '.')}\n${cue.speaker ? `<v ${vttVoice(cue.speaker)}>${cue.text}</v>` : cue.text}\n`)
    .join('\n')}`;

/** "[00:01:23] Name: Line text" per line ("[00:01:23] Line text" when it isn't known who), for reading and pasting. */
export const toPlainText = (lines: TimelineLine[], title?: string): string => {
  const body = lines
    .map((line) => `[${clock(line.start, '.').slice(0, 8)}] ${line.speaker ? `${line.speaker}: ` : ''}${line.text}`)
    .join('\n');
  return title ? `${title}\n\n${body}\n` : `${body}\n`;
};

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', hi: 'Hindi', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian', nl: 'Dutch',
  ja: 'Japanese', ko: 'Korean', zh: 'Chinese', ar: 'Arabic', ru: 'Russian', tr: 'Turkish', pl: 'Polish', sv: 'Swedish',
  ta: 'Tamil', te: 'Telugu', bn: 'Bengali', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi',
  ur: 'Urdu', id: 'Indonesian', vi: 'Vietnamese', th: 'Thai', uk: 'Ukrainian', he: 'Hebrew', fa: 'Persian', tl: 'Filipino',
};

/** "English", or the code itself for a language without a name here. */
export const languageName = (code?: string | null): string | null => {
  if (!code) return null;
  return LANGUAGE_NAMES[code.toLowerCase()] ?? code.toUpperCase();
};
